import { shift } from '@floating-ui/react';
import clsx from 'clsx';
import { format, isDate, parse } from 'date-fns';
import React, { type FC, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DatePickerWrapper } from '@/app/components/form/DateInput/components/DatePickerWrapper';
import { inputStyles, labelStyles } from '../Input/styles';
import { getErrorMessage } from '../utils/getErrorMessage';
import { SinglePickerCustomHeader } from './components/SinglePickerCustomHeader';
import './date-picker.css';
import type { SingleDatePickerProps } from './types';
import CalendarIcon from '/public/icons/calendar.svg';

const FORMAT_DATE_DEFAULT = 'MM/dd/yyyy';

const getSelectedDate = (value: string | Date, dateFormat = FORMAT_DATE_DEFAULT): Date | null => {
  if (!value) return null;
  if (isDate(value)) return value;

  const parsed = parse(value, dateFormat, new Date());
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const SingleDatePicker: FC<SingleDatePickerProps> = ({
  name,
  value,
  onChangeDate,
  label,
  placeholder = 'MM/DD/YYYY',
  required,
  disabled,
  portalId,
  className,
  errors,
  dateFormat = FORMAT_DATE_DEFAULT,
  onBlur,
  renderCustomFooter,
  shouldCloseOnSelect = true,
  intent,
  minDate,
  maxDate,
  startIcon,
}) => {
  const datePickerRef = useRef<DatePicker>(null);
  const rawInputRef = useRef<string>('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const segEditRef = useRef<{ seg: 0 | 1 | 2; firstDigit: string } | null>(null);
  const isSettingValueRef = useRef(false);
  const errorMessage = getErrorMessage(errors, name);
  const selectedDate = getSelectedDate(value, dateFormat);

  const handleChange = (date: Date | null) => {
    onChangeDate(date ? format(date, dateFormat) : '');
    onBlur?.();
  };

  const setInputValue = (target: HTMLInputElement, formatted: string) => {
    rawInputRef.current = formatted;
    isSettingValueRef.current = true;
    const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    nativeSetter?.call(target, formatted);
    target.dispatchEvent(new Event('input', { bubbles: true }));
    requestAnimationFrame(() => {
      isSettingValueRef.current = false;
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement;
    const key = event.key;

    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown'
    ) {
      if (key === 'Backspace') {
        event.preventDefault();
        segEditRef.current = null;

        const actualVal = target.value ?? '';
        if (actualVal && actualVal !== rawInputRef.current) {
          rawInputRef.current = actualVal;
        }

        const currentDigits = rawInputRef.current.replace(/\//g, '');

        if (!currentDigits) return;

        const selStart = target.selectionStart ?? 0;
        const selEnd = target.selectionEnd ?? 0;
        const hasSelection = selStart !== selEnd;

        const clearField = () => {
          rawInputRef.current = '';
          isSettingValueRef.current = true;
          const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
          nativeSetter?.call(target, '');
          target.dispatchEvent(new Event('input', { bubbles: true }));
          requestAnimationFrame(() => {
            isSettingValueRef.current = false;
          });
          onChangeDate('');
          onBlur?.();
        };

        if (hasSelection) {
          clearField();
          return;
        }

        const newDigits = currentDigits.slice(0, -1);

        if (!newDigits) {
          clearField();
          return;
        }

        let formatted = newDigits;
        if (newDigits.length > 2 && newDigits.length <= 4) {
          formatted = newDigits.slice(0, 2) + '/' + newDigits.slice(2);
        } else if (newDigits.length > 4) {
          formatted = newDigits.slice(0, 2) + '/' + newDigits.slice(2, 4) + '/' + newDigits.slice(4);
        }

        setInputValue(target, formatted);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            target.setSelectionRange(formatted.length, formatted.length);
          });
        });
      }
      return;
    }

    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    const selStart = target.selectionStart ?? 0;
    const selEnd = target.selectionEnd ?? 0;
    const hasSelection = selStart !== selEnd;

    const actualValue = target.value ?? '';
    if (actualValue && actualValue !== rawInputRef.current) {
      rawInputRef.current = actualValue;
    }

    const currentRaw = rawInputRef.current;
    const currentDigits = currentRaw.replace(/\//g, '');

    let digits = currentDigits;

    const segDigitRanges: [number, number][] = [
      [0, 2],
      [2, 4],
      [4, 8],
    ];
    const segCharStarts = [0, 3, 6];
    const segCharLens = [2, 2, 4];

    if (segEditRef.current !== null) {
      const { seg, firstDigit } = segEditRef.current;
      const [dStart, dEnd] = segDigitRanges[seg];

      const newSeg = firstDigit + key;

      digits = currentDigits.slice(0, dStart) + newSeg + currentDigits.slice(dEnd);
      segEditRef.current = null;

      const formatted2 = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
      setInputValue(target, formatted2);

      if (seg < 2) {
        const nextSeg = (seg + 1) as 0 | 1 | 2;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            target.setSelectionRange(segCharStarts[nextSeg], segCharStarts[nextSeg] + segCharLens[nextSeg]);
          });
        });
      }

      return;
    }

    if (hasSelection) {
      const getSegment = (charPos: number): 0 | 1 | 2 => {
        if (charPos <= 2) return 0;
        if (charPos <= 5) return 1;
        return 2;
      };

      const segStart = getSegment(selStart);
      const segEnd = getSegment(Math.max(selEnd - 1, selStart));

      if (segStart === segEnd) {
        const seg = segStart;
        const [dStart, dEnd] = segDigitRanges[seg];
        const segLen = dEnd - dStart;

        const newSeg = key.padStart(segLen, '0');
        digits = currentDigits.slice(0, dStart) + newSeg + currentDigits.slice(dEnd);

        segEditRef.current = { seg, firstDigit: key };

        const formatted1 = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
        setInputValue(target, formatted1);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const charStart = segCharStarts[seg];
            target.setSelectionRange(charStart, charStart + segCharLens[seg]);
          });
        });
        return;
      } else {
        segEditRef.current = null;
        digits = key;
      }
    } else {
      segEditRef.current = null;
      digits = currentDigits + key;
    }

    if (digits.length > 8) digits = digits.slice(0, 8);

    let formatted = digits;
    if (digits.length > 2 && digits.length <= 4) {
      formatted = digits.slice(0, 2) + '/' + digits.slice(2);
    } else if (digits.length > 4) {
      formatted = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
    }

    setInputValue(target, formatted);
  };

  const handleChangeRaw = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | undefined) => {
    if (!event) return;

    if (isSettingValueRef.current) return;

    if (event.type === 'click') return;

    const target = event.target as HTMLInputElement;
    const inputValue = target.value;

    if (!inputValue) {
      if (!rawInputRef.current) {
        onChangeDate('');
      }
      return;
    }

    if (inputValue !== rawInputRef.current) {
      rawInputRef.current = inputValue;
    }
  };

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (!datePickerRef.current?.isCalendarOpen()) return;

      const target = e.target as Node;

      if (wrapperRef.current?.contains(target)) return;

      const portalEl = document.getElementById(portalId ?? 'date-picker-root');
      if (portalEl?.contains(target)) return;

      datePickerRef.current?.setOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [portalId]);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    rawInputRef.current = event.target.value;
    segEditRef.current = null;
  };

  const handleClear = () => {
    onChangeDate('');
    datePickerRef.current?.setOpen(false);
  };

  const handleBlur = () => {
    onBlur?.();
    datePickerRef.current?.setOpen(false);
  };

  const handleClose = () => {
    onBlur?.();
    datePickerRef.current?.setOpen(false);
  };

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      {label && (
        <label className={labelStyles({ intent: 'primary', required })} htmlFor={name}>
          {label}
        </label>
      )}
      <div className="relative" ref={wrapperRef}>
        <DatePicker
          ref={datePickerRef}
          selected={selectedDate}
          onChange={handleChange}
          onChangeRaw={handleChangeRaw}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onCalendarClose={handleClose}
          onClickOutside={handleClose}
          onBlur={handleBlur}
          disabled={disabled}
          dateFormatCalendar={dateFormat}
          placeholderText={placeholder}
          dateFormat={dateFormat}
          minDate={minDate}
          maxDate={maxDate}
          showPopperArrow={false}
          strictParsing={false}
          className={inputStyles({
            intent: intent || 'primaryWhite',
            hasStartIcon: true,
            error: !!errorMessage,
            disabled,
          })}
          calendarContainer={(props) => (
            <DatePickerWrapper
              {...props}
              renderCustomFooter={() =>
                renderCustomFooter?.({
                  hasValue: !!selectedDate,
                  disabled,
                  onClear: handleClear,
                  onClose: handleClose,
                })
              }
            />
          )}
          popperPlacement="top"
          popperModifiers={[shift({ padding: 8 })]}
          portalId={portalId ?? 'date-picker-root'}
          renderCustomHeader={(props) => (
            <SinglePickerCustomHeader {...props} hasValue={!!selectedDate} disabled={disabled} />
          )}
          shouldCloseOnSelect={shouldCloseOnSelect}
        />
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {startIcon ?? (
            <CalendarIcon
              className={clsx('w-6 h-6', {
                'text-blue-900': !errorMessage,
                'text-red-500': errorMessage,
              })}
            />
          )}
        </div>
      </div>
      {errorMessage && <p className="text-red-500 text-body-small mt-[6px]">{errorMessage}</p>}
    </div>
  );
};
